/**
 * 明阳智能 Web 后台 - 任务接口通用调用层
 *
 * 协议参考 `/Users/comodo/Documents/明阳智能-web后台/Web管理后台-*.md` 第三章
 * 统一入口 POST /web/agent/sync/task，按 task_name + task_route 分发：
 *   - mingyang_project    项目管理
 *   - mingyang_check      检查项记录管理
 *   - mingyang_knowledge  知识库管理
 *
 * 与旧 work_order 协议的差异：
 *   - 旧协议 output_param 解析后是 { success, message, data }
 *   - 新协议 output_param 解析后即业务数据（错误用外层 status != 200 判定）
 */
import { syncTask as syncTaskAction } from '../../../actions/aiTask'

export type MingyangTaskName =
  | 'mingyang_project'
  | 'mingyang_check'
  | 'mingyang_knowledge'
  | 'mingyang_export'

interface SyncTaskRawResponse {
  data?: {
    status?: number
    msg?: string
    task_id?: string
    task_name?: string
    task_route?: string
    outputParam?: string
    output_param?: string
  }
  code?: number
  message?: string
}

/**
 * 大数精度保护：JS Number 安全范围是 2^53-1（16 位十进制），
 * 后端 snowflake id（17~19 位）直接 JSON.parse 会丢精度。
 * 用 regex 把所有未加引号的 ≥16 位整数（仅 value 位置）包成字符串，
 * 让大数以 string 形式存活到前端。
 */
function safeJsonParse(s: string): any {
  // 仅匹配 JSON value 位置的大整数：
  //   [前缀]: -?\d{16,}
  // 前缀必须是 `:`、`,`、`[` 之后，避免误匹配 key 名或字符串内部
  const wrapped = s.replace(/([:,[])\s*(-?\d{16,})(?=\s*[,}\]])/g, '$1"$2"')
  return JSON.parse(wrapped)
}

function parseOutputParam<T>(raw: unknown): T | null {
  if (raw == null || raw === '') return null
  if (typeof raw === 'object') return raw as T
  if (typeof raw !== 'string') return null
  // 先用大数保护版解析；失败时退回标准 JSON.parse（大数精度可能损失但能用）
  try {
    return safeJsonParse(raw) as T
  } catch (e1) {
    try {
      const r = JSON.parse(raw) as T
      console.warn(
        '[mingyang] safeJsonParse 失败，已退回 JSON.parse（大数可能精度损失）:',
        e1
      )
      return r
    } catch (e2) {
      console.error('[mingyang] 解析 output_param 失败:', e2, '\nraw:', raw)
      return null
    }
  }
}

/**
 * 调用明阳任务接口。
 *
 * @param dispatch  Redux dispatch（从组件 useDispatch 获取，类型用 any 规避 thunk 类型问题）
 * @param taskName  任务大类
 * @param taskRoute 子路由
 * @param input     业务参数对象（内部 JSON.stringify）
 * @throws 网关或后端 status 非 200 时抛 Error，message 取后端 msg
 */
export async function callMingyangTask<T = any>(
  dispatch: (...args: any[]) => any,
  taskName: MingyangTaskName,
  taskRoute: string,
  input: Record<string, any> = {}
): Promise<T> {
  const payload = {
    taskName,
    taskRoute,
    inputParam: JSON.stringify(input)
  }

  const result = (await dispatch(
    syncTaskAction(payload)
  )) as SyncTaskRawResponse

  const innerStatus = result?.data?.status
  const innerMsg = result?.data?.msg
  if (typeof innerStatus === 'number' && innerStatus !== 200) {
    throw new Error(innerMsg || `请求失败 (status=${innerStatus})`)
  }

  const rawOutput = result?.data?.outputParam ?? result?.data?.output_param
  const parsed = parseOutputParam<T>(rawOutput)
  if (parsed === null) {
    if (rawOutput === '' || rawOutput == null) {
      return undefined as unknown as T
    }
    throw new Error('响应解析失败')
  }
  return parsed
}

/**
 * 从 Redux state 取当前用户、团队、身份信息.
 *
 * 用途分级:
 *   - group_id + user_id:写接口(create/update/toggle/export 等)必传
 *   - role:部分读接口(如 mingyang_check.list)需要按身份过滤数据,
 *     后端依赖 inputParam.role 判断当前用户能看哪些记录
 *
 * RTK POJO state 路径（refactor 分支）:
 *   state.group.groupId
 *   state.group.myRole
 *   state.main.user.user_id
 *
 * role 取值(src/constants/config.js memberRoleTypes):
 *   -1=全部成员/未确定, 0=普通成员, 1=主管理员, 2=管理员, 3=部门管理员
 *   ⚠ 必须用 ?? 而不是 ||,否则 myRole===0(普通成员)会被当成 falsy 吞掉
 *   退化成 -1.前后端协议传整数,跟 reducer initialState 一致.
 */
export function selectGroupAndUser(state: any): {
  group_id: string
  user_id: string
  role: number
} {
  const group_id = state?.group?.groupId || ''
  const user_id = state?.main?.user?.user_id || ''
  const role = state?.group?.myRole ?? -1
  return { group_id, user_id, role }
}
